import React, { useEffect, useState, useRef, useImperativeHandle, useMemo, forwardRef, useCallback } from 'react';

import { unpack, pack } from 'msgpackr';
import stringify from '../utils/stableStringify';
import Calculator from '../utils/calculator';
import AntCondition from '../utils/AntCondition';
import AntConfig from '../utils/AntConfig';
import AntLocalization from '../utils/AntLocalization';
import AntBaseForm from './AntBaseForm';
import AntErrorPanel from './AntErrorPanel';
import AntMessageBox from './AntMessageBox';
import AntAuth from '../utils/AntAuth';

import { AntGlobal, AntSession } from '../utils/AntContext';

// Debounce mặc định cho batch flush. Bool (checkbox) flush nhanh hơn text/number
// vì user thường thao tác liên tiếp với cùng nhịp click.
const DEBOUNCE_MS_BOOL = 700;
const DEBOUNCE_MS_OTHER = 1200;

// Gộp request load setting trùng (url+business+group) đang bay → 1 call mạng, share kết quả.
// Chống nhiều AntSettingForm remount đồng thời (DevExtreme Accordion dựng lại item) bắn call
// /listByGroup trùng tham số → BE generate lặp + trả số dòng vênh nhau.
const _inflightSettingLoad = new Map();

export default forwardRef(function (props, ref) {
    const appGlobal = AntGlobal();

    const session = AntSession();

    const busCode = useMemo(() => AntAuth.getSessionInfo('business_code'), []);

    const [rawSettings, setRawSettings] = useState([]);

    // Map code → setting_type (lookup nhanh để chọn debounce window)
    const settingTypeByCode = useMemo(() => {
        var m = {};
        rawSettings.forEach((s) => {
            m[s.code] = s.setting_type;
        });
        return m;
    }, [rawSettings]);

    const initFormItems = () => {
        if (rawSettings.length <= 0) return [];

        var fItems = JSON.parse(stringify(props.items)).filter((i) => rawSettings.some((r) => r.code === i.dataField && r.is_enable));

        rawSettings.forEach((s) => {
            let item = fItems.find((i) => i.dataField === s.code);
            if (item) {
                if (!item.label) item.label = s.name;
                item.disabled = s.can_change !== 1;
            }
        });

        return fItems;
    };
    const formItems = useMemo(() => initFormItems(), [rawSettings, props.items]);

    const initFormData = () => {
        if (rawSettings.length <= 0) return {};

        var obj = { edit: { always_editable_fields: [] } };

        rawSettings.forEach((s) => {
            obj[s.code] = s.setting_value;
            if (s.setting_type == 'bool') obj[s.code] = parseInt(obj[s.code]);
            if (s.can_change === 1) {
                if (props.items.some((i) => i.dataField === s.code)) {
                    obj.edit.always_editable_fields.push(s.code);
                }
            }
        });

        return obj;
    };
    const formData = useMemo(() => initFormData(), [rawSettings, props.items]);

    const [isLoaded, setIsLoaded] = useState(false);
    const [formError, setFormError] = useState({ error: null });
    const [isFlushing, setIsFlushing] = useState(false);

    const containerID = useMemo(() => 'settingContainer-' + Math.floor(Math.random() * 1000), []);

    const form = useRef();
    const msgBox = useRef();
    const isQuerying = useRef(false);
    const loadSequence = useRef(0);
    const loadedRefreshKey = useRef(props.refreshKey);

    // ── Batch state ──────────────────────────────────────────────────────────
    // pendingChanges: Map<code, value> các thay đổi user vừa thao tác, chưa gửi BE.
    const pendingChanges = useRef(new Map());
    // pendingValuesSnapshot: lưu giá trị cũ để rollback khi BE báo lỗi.
    const pendingPrevValues = useRef(new Map());
    const debounceTimer = useRef(null);
    const isUnmounted = useRef(false);

    const showAlert = useCallback((args, onClosedCallback) => {
        if (msgBox.current) msgBox.current.show(args, onClosedCallback);
    }, []);

    const reload = () => {
        loadContent();
    };

    const load = () => {
        setFormError({ error: null });
        loadContent();
    };

    const loadContent = async () => {
        if (isQuerying.current) return;
        if (props.isAllowQuery) {
            var isOK = props.isAllowQuery();
            if (!isOK) return;
        }
        if (busCode) {
            isQuerying.current = true;
            const sequence = ++loadSequence.current;
            var url = props.API.routeURL + (props.settingGroup ? '/listByGroup' : '/list');
            var conds = { ...props.searchConditions, business_code: busCode, setting_group: props.settingGroup };
            if (props.settingCodes) conds.setting_codes = props.settingCodes;
            // Gộp inflight: nhiều form cùng (url+business+group) remount đồng thời → CHỈ 1 request mạng.
            var dedupeKey = url + '|' + busCode + '|' + (props.settingGroup || '') + '|' + JSON.stringify(props.settingCodes || []);
            var result;
            try {
                if (_inflightSettingLoad.has(dedupeKey)) {
                    result = await _inflightSettingLoad.get(dedupeKey);
                } else {
                    var p = props.API.getList(conds, url);
                    _inflightSettingLoad.set(dedupeKey, p);
                    try {
                        result = await p;
                    } finally {
                        _inflightSettingLoad.delete(dedupeKey);
                    }
                }
            } finally {
                isQuerying.current = false;
            }
            if (result && result.rows instanceof Array && result.rows !== null) {
                // A save that started during this read owns the newer snapshot.
                if (sequence === loadSequence.current && !isUnmounted.current) {
                    setRawSettings(result.rows.map(row => pendingChanges.current.has(row.code)
                        ? { ...row, setting_value: typeof pendingChanges.current.get(row.code) === 'boolean' ? Number(pendingChanges.current.get(row.code)) : pendingChanges.current.get(row.code) }
                        : row));
                }
            }
            setIsLoaded(true);
        }
    };

    const setError = async (content, errors) => {
        if (!content && (formError.error === null || formError.error === undefined)) return;
        if (errors == 'TypeError: Failed to fetch')
            errors = {
                vi: 'Không thể kết nối đến hệ thống !',
                en: 'Can not connect to system !',
                fr: 'Impossible de se connecter au système !',
                es: '¡No se puede conectar al sistema!',
                id: 'Tidak dapat terhubung ke sistem!',
                ja: 'システムに接続できません！',
                zh: '无法连接到系统！',
                ko: '시스템에 연결할 수 없습니다!',
                hi: 'सिस्टम से कनेक्ट नहीं हो सकता!',
                ar: 'لا يمكن الاتصال بالنظام!'
            };

        var values = errors === undefined || errors === null ? [] : errors instanceof Array ? errors : [errors];

        content = values.length === 1 ? values[0] : content;

        setFormError({
            error: content
                ? {
                      text: String(content),
                      values: values
                  }
                : null
        });
        if (props.onError) props.onError(content, errors);
    };

    const cantSaveText = () =>
        AntLocalization.translateText({
            vi: 'Không thể cập nhật thông tin !',
            en: 'Can not save data !',
            fr: 'Échec de l\'enregistrement !',
            es: '¡Error al guardar!',
            id: 'Tidak dapat menyimpan data!',
            ja: 'データを保存できません！',
            zh: '无法保存数据！',
            ko: '데이터를 저장할 수 없습니다!',
            hi: 'डेटा सहेजा नहीं जा सकता!',
            ar: 'لا يمكن حفظ البيانات!'
        });

    // ── BATCH FLUSH ──────────────────────────────────────────────────────────
    // Lấy toàn bộ pending → 1 request /batch-update → BE gộp side-effects, SYNC
    // recalc, trả snapshot fresh. Trên FE: optimistic giữ nguyên (DevExtreme đã
    // hiển thị value mới); chỉ setRawSettings 1 lần khi BE trả về để cập nhật
    // is_enable / cascade visibility.
    const flushBatch = useCallback(async () => {
        if (debounceTimer.current) {
            clearTimeout(debounceTimer.current);
            debounceTimer.current = null;
        }
        if (pendingChanges.current.size <= 0) return;

        var changes = Array.from(pendingChanges.current.entries()); // [[code, value], ...]
        ++loadSequence.current;
        pendingChanges.current.clear();

        var objs = changes.map(([code, value]) => {
            var v = value;
            if (typeof v === 'boolean') v = v ? 1 : 0;
            return { code: code, setting_value: v };
        });

        setError(null);
        setIsFlushing(true);

        var result = null;
        var error = null;
        try {
            result = await props.API.postBatchUpdate(objs, {
                ...props.searchConditions,
                business_code: busCode,
                setting_group: props.settingGroup
                , ...(props.settingCodes ? { setting_codes: props.settingCodes } : {})
            });
        } catch (err) {
            error = err;
        }

        if (isUnmounted.current) return;

        setIsFlushing(false);

        var hasError = !result || result.error || (result.updated_qty !== undefined && result.updated_qty < objs.length);
        if (hasError) {
            error = error || (result && result.error) || cantSaveText();

            // Rollback: đẩy giá trị cũ vào pending để form lấy lại trên reload.
            // Đơn giản nhất là reload toàn bộ rawSettings từ BE.
            setError(cantSaveText(), error);
            if (props.onError) props.onError(cantSaveText(), error);
            pendingPrevValues.current.clear();
            // Reload để lấy state thực từ BE → form hiển thị đúng.
            reload();
            return;
        }

        pendingPrevValues.current.clear();

        // Refresh token chỉ 1 lần / batch nếu BE báo có module thay đổi.
        var needRefresh = props.autoRefreshToken && Boolean(result.refreshTokenRequired);

        var applySnapshot = () => {
            // Giữ scroll khi setRawSettings re-mount form (is_enable cascade hide/show)
            preserveScroll();
            if (result.rows instanceof Array) {
                // Merge pendingChanges (clicks user vừa thực hiện trong lúc request đang
                // bay) lên snapshot fresh, tránh ghi đè giá trị optimistic mới nhất.
                var rows = result.rows.map((s) => {
                    if (pendingChanges.current.has(s.code)) {
                        var v = pendingChanges.current.get(s.code);
                        if (typeof v === 'boolean') v = v ? 1 : 0;
                        return { ...s, setting_value: v };
                    }
                    return s;
                });
                setRawSettings(rows);
            }
            if (session && session.onSessionChanged) session.onSessionChanged(result);
            if (props.onSaved) props.onSaved(result);
        };

        if (needRefresh) {
            AntAuth.refreshToken(false, null, null, () => {
                if (isUnmounted.current) return;
                applySnapshot();
            });
        } else {
            applySnapshot();
        }
    }, [props.API, props.searchConditions, props.settingGroup, props.settingCodes, props.autoRefreshToken, busCode, session]);

    const scheduleFlush = useCallback(
        (codeForType) => {
            var type = settingTypeByCode[codeForType];
            var delay = type === 'bool' ? DEBOUNCE_MS_BOOL : DEBOUNCE_MS_OTHER;

            if (debounceTimer.current) clearTimeout(debounceTimer.current);
            debounceTimer.current = setTimeout(() => {
                debounceTimer.current = null;
                flushBatch();
            }, delay);
        },
        [settingTypeByCode, flushBatch]
    );

    // Tìm scrollable ancestor để lưu/restore scrollTop quanh click (tránh nhảy do
    // AntBaseForm re-eval items theo formData khi DevExtreme update internal state).
    const getScrollableAncestor = useCallback((el) => {
        var node = el && el.parentElement;
        while (node && node !== document.body) {
            var style = window.getComputedStyle(node);
            var oy = style.overflowY;
            if ((oy === 'auto' || oy === 'scroll') && node.scrollHeight > node.clientHeight) return node;
            node = node.parentElement;
        }
        return window;
    }, []);

    const preserveScroll = useCallback(() => {
        var container = document.getElementById(containerID);
        var scroller = getScrollableAncestor(container);
        var top = scroller === window ? window.scrollY : scroller.scrollTop;

        // DevExtreme Form remount items + có thể focus.scrollIntoView → scroll nhảy.
        // Lock scroll trong ~400ms: nếu scrollTop bị thay đổi do re-render thì kéo về.
        // Cho phép user scroll thật sự bằng cách bắt wheel/touch/keydown để bỏ lock sớm.
        var apply = () => {
            if (scroller === window) {
                if (window.scrollY !== top) window.scrollTo({ top: top, left: 0, behavior: 'auto' });
            } else {
                if (scroller.scrollTop !== top) scroller.scrollTop = top;
            }
        };

        var released = false;
        var release = () => {
            released = true;
            (scroller === window ? window : scroller).removeEventListener('wheel', release);
            (scroller === window ? window : scroller).removeEventListener('touchstart', release);
            (scroller === window ? window : scroller).removeEventListener('keydown', release);
        };
        (scroller === window ? window : scroller).addEventListener('wheel', release, { passive: true, once: true });
        (scroller === window ? window : scroller).addEventListener('touchstart', release, { passive: true, once: true });
        (scroller === window ? window : scroller).addEventListener('keydown', release, { once: true });

        var deadline = Date.now() + 400;
        var tick = () => {
            if (released) return;
            apply();
            if (Date.now() < deadline) requestAnimationFrame(tick);
            else release();
        };
        requestAnimationFrame(tick);
    }, [containerID, getScrollableAncestor]);

    const onFieldDataChanged = async (e) => {
        if (props.onFieldDataChanged) {
            var isValid = props.onFieldDataChanged(e);
            if (!isValid) return;
        }

        var keys = Object.keys(e);
        if (keys.length <= 0) return;

        var field = keys[0];
        var value = e[field];

        // Ghim scroll TRƯỚC khi AntBaseForm useEffect[formData] kích hoạt re-render
        // (DevExtreme onValueChanged → setFormData internal → setFormItems → items mới
        // → DOM resize → scroll nhảy). preserveScroll dùng rAF nên không block UI.
        preserveScroll();

        // Lưu giá trị cũ (nếu chưa có) để rollback.
        if (!pendingPrevValues.current.has(field)) {
            pendingPrevValues.current.set(field, formData[field]);
        }
        pendingChanges.current.set(field, value);

        scheduleFlush(field);
    };

    const getField = (field) => {
        // Ưu tiên giá trị pending nếu có (FE đã optimistic, BE chưa apply).
        if (pendingChanges.current.has(field)) return pendingChanges.current.get(field);
        return formData[field];
    };

    useImperativeHandle(ref, () => ({
        getField: getField,
        flush: flushBatch,
        reload: reload
    }));

    useEffect(() => {
        isUnmounted.current = false;
        reload();
        return () => {
            isUnmounted.current = true;
            // Best-effort: nếu user rời màn hình mà còn pending, gửi flush ngay.
            if (debounceTimer.current) clearTimeout(debounceTimer.current);
            if (pendingChanges.current.size > 0) {
                // Fire-and-forget — không await được trong cleanup.
                flushBatch();
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (loadedRefreshKey.current === props.refreshKey || !isLoaded) return;
        // Refresh sibling groups after a settings save without remounting them or
        // losing edits still waiting for the debounce/save in another group.
        if (isFlushing || pendingChanges.current.size > 0 || isQuerying.current) return;
        loadedRefreshKey.current = props.refreshKey;
        reload();
    }, [props.refreshKey, isLoaded, isFlushing]);

    useEffect(() => {
        if (!props.onHasVisibleItems || !isLoaded) return;
        var hasVisible = formItems.some((item) => AntCondition.isVisible(formData, item) !== false);
        props.onHasVisibleItems(hasVisible);
    }, [isLoaded, formItems, formData, session, props.onHasVisibleItems]);

    return (
        <React.Fragment>
            <div
                id={containerID}
                className={props.showAsCardField || props.form?.showAsCardField ? 'ant-card-form' : ''}
                style={{ padding: '10px', borderRadius: '4px', position: 'relative' }}
            >
                {formError.error && (
                    <AntErrorPanel errors={formError.error.values} id={`ant-voucher-error-${props.API.entityName}`} text={formError.error.text} />
                )}
                {formError.error && <p style={{ marginBlockStart: '0px', marginBlockEnd: '10px' }} />}
                {formError.error && <p />}

                <AntBaseForm
                    ref={form}
                    API={props.API}
                    type={'Update'}
                    formData={formData}
                    items={formItems}
                    bypassReevalOnFormDataChange={true}
                    defaultSelectShowClearButton={false}
                    alignItemLabelsInAllGroups={props.alignItemLabelsInAllGroups}
                    labelLocation={props.labelLocation}
                    allCheckBoxLeft={props.allCheckBoxLeft}
                    colCount={props.colCount !== undefined ? props.colCount : 1}
                    colCountDefault={props.colCountDefault}
                    fields={props.fields ? props.fields : []}
                    onFieldDataChanged={onFieldDataChanged}
                    onError={setError}
                    disabled={props.disabled !== undefined ? props.disabled : undefined}
                />

                {props.showLoading && isFlushing && (
                    <div
                        style={{
                            position: 'absolute',
                            top: 8,
                            right: 12,
                            fontSize: 12,
                            color: '#64748b',
                            background: 'rgba(255,255,255,0.85)',
                            padding: '2px 8px',
                            borderRadius: 4,
                            pointerEvents: 'none',
                            transition: 'opacity 0.15s ease'
                        }}
                    >
                        {AntLocalization.translateText({
                            vi: 'Đang lưu…',
                            en: 'Saving…',
                            fr: 'Sauvegarde…',
                            es: 'Guardando…',
                            id: 'Menyimpan…',
                            ja: '保存中…',
                            zh: '保存中…',
                            ko: '저장 중…',
                            hi: 'सहेज रहा है…',
                            ar: 'جارٍ الحفظ…'
                        })}
                    </div>
                )}
                <AntMessageBox ref={msgBox} />
            </div>
        </React.Fragment>
    );
});
